import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Modal, 
  Form, 
  Alert, 
  Table,
  Badge,
  Image
} from 'react-bootstrap';
import axios from 'axios';

const ProjectManager = () => {
  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    titolo: '',
    descrizione: '',
    categoria: '',
    cliente: '',
    dataInizio: '',
    dataFine: '',
    stato: 'in_corso',
    immagine: null,
    immaginiAggiuntive: [],
    year: '',
    location: '',
    squareMeters: '',
    projectValue: '',
    features: '',
    // Campi per traduzioni inglesi
    titolo_en: '',
    descrizione_en: '',
    categoria_en: '',
    cliente_en: '',
    stato_en: '',
    features_en: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'https://cogeis.onrender.com';
      // Aumentiamo il limite per mostrare tutti i progetti nella pagina admin
      const response = await axios.get(`${apiUrl}/api/projects?lang=it&limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data.data);
    } catch (err) {
      console.error('Errore nel caricamento dei progetti:', err);
      let errorMessage = 'Errore nel caricamento dei progetti';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'immagine' && files[0]) {
      setFormData({ ...formData, immagine: files[0] });
      setImagePreview(URL.createObjectURL(files[0]));
    } else if (name === 'immaginiAggiuntive' && files.length > 0) {
      setFormData({ ...formData, immaginiAggiuntive: Array.from(files) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validazione campi obbligatori
    const requiredFields = {
      titolo: 'Titolo',
      descrizione: 'Descrizione',
      categoria: 'Categoria',
      stato: 'Stato',
      year: 'Anno di Realizzazione'
    };
    
    const missingFields = [];
    const validationErrors = [];
    
    Object.keys(requiredFields).forEach(field => {
      if (!formData[field] || formData[field].toString().trim() === '') {
        missingFields.push(requiredFields[field]);
      }
    });
    
    // Validazione specifica per la descrizione (minimo 10 caratteri)
    if (formData.descrizione && formData.descrizione.trim().length < 10) {
      validationErrors.push('La descrizione deve essere di almeno 10 caratteri');
    }
    
    // Validazione specifica per il titolo (minimo 3 caratteri)
    if (formData.titolo && formData.titolo.trim().length < 3) {
      validationErrors.push('Il titolo deve essere di almeno 3 caratteri');
    }
    
    // Validazione per stato_en (se compilato, deve essere un valore valido)
    if (formData.stato_en && !['in_progress', 'completed', 'pending', 'cancelled'].includes(formData.stato_en)) {
      validationErrors.push('Stato (EN) non valido. Valori permessi: in_progress, completed, pending, cancelled');
    }
    
    if (missingFields.length > 0) {
      setError(`I seguenti campi sono obbligatori: ${missingFields.join(', ')}`);
      return;
    }
    
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');
      
      const formDataToSend = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && key !== 'immaginiAggiuntive') {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Aggiungi le immagini aggiuntive
      if (formData.immaginiAggiuntive && formData.immaginiAggiuntive.length > 0) {
        formData.immaginiAggiuntive.forEach(file => {
          formDataToSend.append('immaginiAggiuntive', file);
        });
      }



      if (editingProject) {
        await axios.put(
          `${process.env.REACT_APP_API_URL || 'https://cogeis.onrender.com'}/api/projects/${editingProject._id}`,
          formDataToSend,
          {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        setSuccess('Progetto aggiornato con successo!');
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL || 'https://cogeis.onrender.com'}/api/projects`,
          formDataToSend,
          {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        setSuccess('Progetto creato con successo!');
      }

      handleCloseModal();
      fetchProjects();
    } catch (err) {
      console.error('Errore durante il salvataggio:', err);
      console.error('Dettagli errore:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        headers: err.response?.headers
      });
      
      
      let errorMessage = 'Errore durante il salvataggio';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data?.errors) {
        // Gestione errori di validazione dettagliati
        const validationErrors = err.response.data.errors;
        if (Array.isArray(validationErrors)) {
          errorMessage = validationErrors.map(error => error.msg || error.message).join(', ');
        } else if (typeof validationErrors === 'object') {
          errorMessage = Object.values(validationErrors).flat().join(', ');
        }
      } else if (err.response?.data) {
        // Se c'è una risposta ma non rientra nei casi precedenti
        errorMessage = `Errore del server: ${JSON.stringify(err.response.data)}`;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setError(''); // Pulisci gli errori precedenti
    setFormData({
      titolo: project.titolo || '',
      descrizione: project.descrizione || '',
      categoria: project.categoria || '',
      cliente: project.cliente || '',
      dataInizio: project.dataInizio ? project.dataInizio.split('T')[0] : '',
      dataFine: project.dataFine ? project.dataFine.split('T')[0] : '',
      stato: project.stato || 'in_corso',
      immagine: null,
      immaginiAggiuntive: [],
      year: project.year || '',
      location: project.location || '',
      squareMeters: project.squareMeters || '',
      projectValue: project.projectValue || '',
      features: project.features ? project.features.join(', ') : '',
      // Campi per traduzioni inglesi
      titolo_en: project.titolo_en || '',
      descrizione_en: project.descrizione_en || '',
      categoria_en: project.categoria_en || '',
      cliente_en: project.cliente_en || '',
      stato_en: project.stato_en || '',
      features_en: project.features_en ? project.features_en.join(', ') : ''
    });
    setImagePreview(project.immagine);
    setShowModal(true);
  };

  const handleDelete = async (projectId) => {
    if (window.confirm('Sei sicuro di voler eliminare questo progetto?')) {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        await axios.delete(
          `${process.env.REACT_APP_API_URL || 'https://cogeis.onrender.com'}/api/projects/${projectId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setSuccess('Progetto eliminato con successo!');
        fetchProjects();
      } catch (err) {
        console.error('Errore durante l\'eliminazione del progetto:', err);
        let errorMessage = 'Errore durante l\'eliminazione del progetto';
        
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProject(null);
    setFormData({
      titolo: '',
      descrizione: '',
      categoria: '',
      cliente: '',
      dataInizio: '',
      dataFine: '',
      stato: 'in_corso',
      immagine: null,
      immaginiAggiuntive: [],
      year: '',
      location: '',
      squareMeters: '',
      projectValue: '',
      features: '',
      // Campi per traduzioni inglesi
      titolo_en: '',
      descrizione_en: '',
      categoria_en: '',
      cliente_en: '',
      stato_en: '',
      features_en: ''
    });
    setImagePreview(null);
    setError('');
  };

  const getStatusBadge = (stato) => {
    const statusMap = {
      'in_corso': { variant: 'warning', text: 'In Corso' },
      'completato': { variant: 'success', text: 'Completato' },
      'in_attesa': { variant: 'info', text: 'In Attesa' },
      'annullato': { variant: 'danger', text: 'Annullato' }
    };
    const status = statusMap[stato] || { variant: 'secondary', text: stato };
    return <Badge bg={status.variant}>{status.text}</Badge>;
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2>Gestione Progetti</h2>
              {projects.length > 0 && (
                <p className="text-muted mb-0">
                  Totale progetti: <strong>{projects.length}</strong>
                </p>
              )}
            </div>
            <Button 
              variant="primary" 
              onClick={() => {
                setError(''); // Pulisci gli errori precedenti
                setShowModal(true);
              }}
              disabled={loading}
            >
              <i className="fas fa-plus me-2"></i>
              Nuovo Progetto
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3">
          <Alert.Heading>Errore!</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')} className="mb-3">
          <Alert.Heading>Successo!</Alert.Heading>
          <p>{success}</p>
        </Alert>
      )}

      <Row>
        <Col>
          <Card>
            <Card.Body>
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Caricamento...</span>
                  </div>
                </div>
              ) : (
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Immagine</th>
                      <th>Titolo</th>
                      <th>Categoria</th>
                      <th>Cliente</th>
                      <th>Stato</th>
                      <th>Anno</th>
                      <th>Località</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((project) => (
                      <tr key={project._id}>
                        <td>
                          {project.immagine && (
                            <Image 
                              src={project.immagine} 
                              alt={project.titolo}
                              width="50"
                              height="50"
                              className="rounded"
                              style={{ objectFit: 'cover' }}
                            />
                          )}
                        </td>
                        <td>
                          <div>
                            <strong>{project.titolo}</strong>
                            {project.descrizione && (
                              <div className="text-muted small">
                                {project.descrizione.length > 50 
                                  ? `${project.descrizione.substring(0, 50)}...` 
                                  : project.descrizione}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          {project.categoria === 'ristrutturazione' && 'Ristrutturazione Fabbricati'}
                          {project.categoria === 'commerciale' && 'Negozi-Hotel-Servizi'}
                          {project.categoria === 'residenziale' && 'Ristrutturazione d\'Interni'}
                          {!['ristrutturazione', 'commerciale', 'residenziale'].includes(project.categoria) && project.categoria}
                        </td>
                        <td>{project.cliente || '-'}</td>
                        <td>{getStatusBadge(project.stato)}</td>
                        <td>{project.year || '-'}</td>
                        <td>{project.location || '-'}</td>
                        <td>
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="me-2"
                            onClick={() => handleEdit(project)}
                            title="Modifica"
                          >
                            <i className="fas fa-edit"></i>
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDelete(project._id)}
                            title="Elimina"
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal per aggiunta/modifica progetto */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingProject ? 'Modifica Progetto' : 'Nuovo Progetto'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && (
              <Alert variant="danger" className="mb-3">
                <Alert.Heading>Errore!</Alert.Heading>
                <p>{error}</p>
              </Alert>
            )}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Titolo <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="titolo"
                    value={formData.titolo}
                    onChange={handleInputChange}
                    placeholder="Inserisci il titolo del progetto"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Categoria <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="categoria"
                    value={formData.categoria}
                    onChange={handleInputChange}
                  >
                    <option value="">Seleziona una categoria</option>
                    <option value="ristrutturazione">Ristrutturazione Fabbricati</option>
                    <option value="commerciale">Negozi-Hotel-Servizi</option>
                    <option value="residenziale">Ristrutturazione d'Interni</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Descrizione <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="descrizione"
                value={formData.descrizione}
                onChange={handleInputChange}
                placeholder="Descrivi il progetto in dettaglio (minimo 10 caratteri)"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cliente</Form.Label>
                  <Form.Control
                    type="text"
                    name="cliente"
                    value={formData.cliente}
                    onChange={handleInputChange}
                    placeholder="Nome del cliente"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Stato <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="stato"
                    value={formData.stato}
                    onChange={handleInputChange}
                  >
                    <option value="in_corso">In Corso</option>
                    <option value="completato">Completato</option>
                    <option value="in_attesa">In Attesa</option>
                    <option value="annullato">Annullato</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Data Inizio</Form.Label>
                  <Form.Control
                    type="date"
                    name="dataInizio"
                    value={formData.dataInizio}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Data Fine</Form.Label>
                  <Form.Control
                    type="date"
                    name="dataFine"
                    value={formData.dataFine}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Anno di Realizzazione <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    name="year"
                    value={formData.year}
                    onChange={handleInputChange}
                    placeholder="Es: 2024"
                    min="1900"
                    max="2100"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Località</Form.Label>
                  <Form.Control
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Es: Milano, Roma, Napoli"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Metri Quadrati</Form.Label>
                  <Form.Control
                    type="number"
                    name="squareMeters"
                    value={formData.squareMeters}
                    onChange={handleInputChange}
                    placeholder="Es: 150"
                    min="0"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Valore Progetto (€)</Form.Label>
                  <Form.Control
                    type="number"
                    name="projectValue"
                    value={formData.projectValue}
                    onChange={handleInputChange}
                    placeholder="Es: 50000"
                    min="0"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Caratteristiche</Form.Label>
              <Form.Control
                type="text"
                name="features"
                value={formData.features}
                onChange={handleInputChange}
                placeholder="Es: Design moderno, Materiali sostenibili, Illuminazione LED (separate da virgole)"
              />
            </Form.Group>

            {/* Sezione Traduzioni Inglesi */}
            <hr className="my-4" />
            <h6 className="text-muted mb-3">Traduzioni Inglesi (opzionali)</h6>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Titolo (EN)</Form.Label>
                  <Form.Control
                    type="text"
                    name="titolo_en"
                    value={formData.titolo_en}
                    onChange={handleInputChange}
                    placeholder="Es: Modern House Project"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cliente (EN)</Form.Label>
                  <Form.Control
                    type="text"
                    name="cliente_en"
                    value={formData.cliente_en}
                    onChange={handleInputChange}
                    placeholder="Es: John Smith"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Descrizione (EN)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="descrizione_en"
                value={formData.descrizione_en}
                onChange={handleInputChange}
                placeholder="Es: Modern residential project with sustainable materials"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Categoria (EN)</Form.Label>
                  <Form.Control
                    type="text"
                    name="categoria_en"
                    value={formData.categoria_en}
                    onChange={handleInputChange}
                    placeholder="Es: residential, commercial, renovation"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Stato (EN)</Form.Label>
                  <Form.Select
                    name="stato_en"
                    value={formData.stato_en}
                    onChange={handleInputChange}
                  >
                    <option value="">Seleziona uno stato</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="cancelled">Cancelled</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Caratteristiche (EN)</Form.Label>
              <Form.Control
                type="text"
                name="features_en"
                value={formData.features_en}
                onChange={handleInputChange}
                placeholder="Es: Modern design, Sustainable materials, LED lighting (separate by commas)"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Immagine Principale</Form.Label>
              <Form.Control
                type="file"
                name="immagine"
                onChange={handleInputChange}
                accept="image/*"
              />
              {imagePreview && (
                <div className="mt-2">
                  <Image 
                    src={imagePreview} 
                    alt="Preview" 
                    width="200" 
                    className="rounded"
                  />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Immagini Aggiuntive (max 3)</Form.Label>
              <Form.Control
                type="file"
                name="immaginiAggiuntive"
                onChange={handleInputChange}
                accept="image/*"
                multiple
              />
              <Form.Text className="text-muted">
                Puoi selezionare fino a 3 immagini aggiuntive
              </Form.Text>
              {formData.immaginiAggiuntive && formData.immaginiAggiuntive.length > 0 && (
                <div className="mt-2">
                  <p className="text-muted">Immagini selezionate: {formData.immaginiAggiuntive.length}</p>
                </div>
              )}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Annulla
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? 'Salvataggio...' : (editingProject ? 'Aggiorna' : 'Crea')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default ProjectManager; 